import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { L10nLocale, L10N_LOCALE, L10nTranslatePipe } from 'angular-l10n';
import { MatDialogTitle, MatDialogContent } from '@angular/material/dialog';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { AssetAttribute } from '../../hickory-api';

/**
 * Modal dialog component for selecting a custom asset attribute to filter by.
 *
 * This component displays a list of available asset attributes (custom fields defined
 * in the system) and allows the user to select one to create an association filter.
 * Once selected, the attribute becomes available for value selection via SelectAssociationValuesComponent.
 *
 * @example
 * // Used within associations.component.html
 * <app-associations-select-asset-attributes
 *   [attributes]="assetAttributesList"
 *   [selectedAssetAttributes]="selectedAssetAttributes"
 *   (selected)="onSelectAssetAttributeUpdated($event)">
 * </app-associations-select-asset-attributes>
 */
@Component({
  selector: 'app-associations-select-asset-attributes',
  templateUrl: './select-asset-attributes.component.html',
  styleUrls: ['./select-asset-attributes.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [MatDialogTitle, CdkScrollable, MatDialogContent, L10nTranslatePipe],
})
export class SelectAssetAttributesComponent {
  readonly locale = inject<L10nLocale>(L10N_LOCALE);

  @Input() attributes: AssetAttribute[];
  @Input() selectedAssetAttributes: AssetAttribute[];
  @Output() selected: EventEmitter<AssetAttribute> = new EventEmitter();

  isSelected(attribute: AssetAttribute) {
    return this.selectedAssetAttributes.some(a => a.id === attribute.id);
  }

  onSelect(attribute: AssetAttribute) {
    this.selected.emit(attribute);
  }
}
